import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Cpu,
  Activity,
  Zap,
  Brain,
  ThermometerSun,
  HardDrive,
  RefreshCw,
  Play,
  Pause,
  AlertTriangle,
  CheckCircle,
  Clock,
  BarChart3,
  Terminal,
  Key,
  Shield,
  ArrowRightLeft
} from 'lucide-react';
import { toast } from '../../utils/toastUtils';
import apiClient from '../../services/api/client';
import { useI18n } from '../../i18n/I18nContext';

// GPU Status Card
const GPUStatusCard = ({ gpuData, t }) => {
  const vramPercent = gpuData?.vram_total_mb
    ? ((gpuData.vram_used_mb / gpuData.vram_total_mb) * 100).toFixed(1)
    : 0;

  const tempColor = gpuData?.temperature_c > 80
    ? 'text-red-400'
    : gpuData?.temperature_c > 60
      ? 'text-yellow-400'
      : 'text-green-400';

  return (
    <div className="liquid-glass rounded-xl p-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-white flex items-center gap-2">
          <Cpu className="w-5 h-5 text-brand-green" />
          {t('llmMonitor.gpuStatus')}
        </h3>
        <span className="text-sm text-gray-400 truncate max-w-[200px]" title={gpuData?.gpu_name || 'RTX 5070 Ti'}>
          {gpuData?.gpu_name || 'RTX 5070 Ti'}
        </span>
      </div>

      <div className="grid grid-cols-2 gap-4 mb-4">
        {/* VRAM Usage */}
        <div className="bg-black/30 rounded-lg p-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm text-gray-400">{t('llmMonitor.vram')}</span>
            <span className="text-sm text-brand-green">{vramPercent}%</span>
          </div>
          <div className="h-2 bg-gray-700 rounded-full overflow-hidden">
            <div
              className="h-full bg-gradient-to-r from-brand-green to-cyan-400 transition-all duration-500"
              style={{ width: `${vramPercent}%` }}
            />
          </div>
          <div className="text-xs text-gray-500 mt-1">
            {gpuData?.vram_used_mb || 0} / {gpuData?.vram_total_mb || 16384} MB
          </div>
        </div>

        {/* GPU Utilization */}
        <div className="bg-black/30 rounded-lg p-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm text-gray-400">{t('llmMonitor.utilization')}</span>
            <span className="text-sm text-brand-green">{gpuData?.utilization_percent || 0}%</span>
          </div>
          <div className="h-2 bg-gray-700 rounded-full overflow-hidden">
            <div
              className="h-full bg-gradient-to-r from-purple-500 to-pink-500 transition-all duration-500"
              style={{ width: `${gpuData?.utilization_percent || 0}%` }}
            />
          </div>
        </div>
      </div>

      <div className="flex items-center justify-between text-sm">
        <div className={`flex items-center gap-2 ${tempColor}`}>
          <ThermometerSun className="w-4 h-4" />
          {gpuData?.temperature_c || 0}°C
        </div>
        <div className="flex items-center gap-2 text-yellow-400">
          <Zap className="w-4 h-4" />
          {gpuData?.power_draw_w?.toFixed(1) || 0}W
        </div>
        <div className="flex items-center gap-2 text-brand-green min-w-0">
          <Brain className="w-4 h-4 flex-shrink-0" />
          <span className="truncate" title={gpuData?.current_model?.split(':')[0] || t('llmMonitor.noModel')}>
            {gpuData?.current_model?.split(':')[0] || t('llmMonitor.noModel')}
          </span>
        </div>
      </div>
    </div>
  );
};

// Model Status Card
const ModelCard = ({ model, isActive, onSwap, t }) => {
  const roleColors = {
    'Tactical Coordinator': 'from-cyan-500 to-blue-500',
    'Security Expert': 'from-purple-500 to-pink-500',
    'Deep Analyst': 'from-orange-500 to-red-500',
    'Strategic Commander': 'from-yellow-500 to-green-500',
    'General': 'from-gray-500 to-gray-600'
  };

  return (
    <div className={`liquid-glass rounded-xl p-4 border-2 transition-all ${
      isActive ? 'border-brand-green' : 'border-transparent hover:border-brand-green/30'
    }`}>
      <div className="flex items-center justify-between mb-3 gap-2">
        <div className="flex items-center gap-2 min-w-0 flex-1">
          <div className={`w-2 h-2 rounded-full flex-shrink-0 ${isActive ? 'bg-green-400 animate-pulse' : 'bg-gray-500'}`} />
          <h4 className="font-medium text-white truncate" title={model.name}>{model.name}</h4>
        </div>
        <span className={`text-xs px-2 py-1 rounded-full bg-gradient-to-r flex-shrink-0 ${roleColors[model.role] || 'from-gray-500 to-gray-600'} text-white`}>
          {model.role}
        </span>
      </div>

      <p className="text-sm text-gray-400 mb-3 line-clamp-2">
        {model.description}
      </p>

      <div className="flex items-center justify-between text-xs text-gray-500">
        <span className="flex items-center gap-1">
          <HardDrive className="w-3 h-3" />
          {model.location === 'GPU'
            ? `${(model.vram_required_mb / 1024).toFixed(1)}GB ${t('llmMonitor.vram')}`
            : `${(model.ram_required_mb / 1024).toFixed(1)}GB RAM`
          }
        </span>
        {model.location === 'GPU' && (
          isActive ? (
            <span className="flex items-center gap-1 text-green-400">
              <CheckCircle className="w-3 h-3" />
              {t('llmMonitor.loaded')}
            </span>
          ) : (
            <button
              onClick={() => onSwap(model.id)}
              className="flex items-center gap-1 text-brand-green hover:text-white transition-colors"
            >
              <ArrowRightLeft className="w-3 h-3" />
              {t('llmMonitor.load')}
            </button>
          )
        )}
      </div>
    </div>
  );
};

// Session Card
const SessionCard = ({ session, onClick, t }) => {
  return (
    <div
      className="liquid-glass rounded-lg p-4 cursor-pointer hover:border-brand-green/30 border-2 border-transparent transition-all"
      onClick={() => onClick(session)}
    >
      <div className="flex items-center justify-between mb-2">
        <span className="font-mono text-sm text-brand-green">{session.session_id}</span>
        <span className={`text-xs px-2 py-1 rounded-full ${
          session.current_phase === 'completed'
            ? 'bg-green-500/20 text-green-400'
            : 'bg-blue-500/20 text-blue-400'
        }`}>
          {session.current_phase}
        </span>
      </div>

      <div className="text-sm text-gray-400 truncate mb-2">
        {session.target || t('llmMonitor.noTarget')}
      </div>

      <div className="flex items-center gap-4 text-xs text-gray-500">
        <span className="flex items-center gap-1">
          <Terminal className="w-3 h-3" />
          {session.stats?.total_entries || 0} {t('llmMonitor.entries')}
        </span>
        <span className="flex items-center gap-1">
          <AlertTriangle className="w-3 h-3" />
          {session.stats?.vulns_discovered || 0} {t('llmMonitor.vulnerabilities')}
        </span>
        <span className="flex items-center gap-1">
          <Key className="w-3 h-3" />
          {session.stats?.creds_found || 0} {t('llmMonitor.credentials')}
        </span>
      </div>
    </div>
  );
};

// GPU Pool Card
const GPUPoolCard = ({ pool, servers, t }) => {
  if (!pool || !servers) return null;

  return (
    <div className="liquid-glass rounded-xl p-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-white flex items-center gap-2">
          <Activity className="w-5 h-5 text-brand-green" />
          {t('llmMonitor.poolStatus')}
        </h3>
        <span className={`text-sm px-3 py-1 rounded-full ${
          pool.healthy_servers === pool.total_servers
            ? 'bg-green-500/20 text-green-400'
            : 'bg-yellow-500/20 text-yellow-400'
        }`}>
          {pool.healthy_servers}/{pool.total_servers} {t('llmMonitor.servers')}
        </span>
      </div>

      <div className="space-y-3">
        {servers.map((server, idx) => (
          <div key={idx} className="bg-black/30 rounded-lg p-3">
            <div className="flex items-center justify-between mb-2 gap-2">
              <div className="flex items-center gap-2 min-w-0 flex-1">
                <div className={`w-2 h-2 rounded-full flex-shrink-0 ${
                  server.healthy ? 'bg-green-400 animate-pulse' : 'bg-red-400'
                }`} />
                <span className="text-sm text-gray-300 font-mono truncate" title={server.url}>
                  {server.url.replace('http://', '').replace('https://', '')}
                </span>
              </div>
              <span className="text-xs text-gray-500 flex-shrink-0">
                {server.response_time_ms?.toFixed(0) || 0} ms
              </span>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              {(server.models_loaded || []).slice(0, 4).map((model, i) => (
                <span key={i} className="text-xs px-2 py-1 bg-brand-green/20 text-brand-green rounded truncate max-w-[120px]" title={model}>
                  {model.split(':')[0]}
                </span>
              ))}
              {(server.models_loaded || []).length > 4 && (
                <span className="text-xs text-gray-500">
                  +{server.models_loaded.length - 4} {t('llmMonitor.moreModels')}
                </span>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// Metrics Card
const MetricsCard = ({ metrics, t }) => {
  // Format duration with proper French formatting
  const formatSwapTime = (seconds) => {
    if (!seconds || seconds < 1) return '< 1 s';
    if (seconds < 60) return `${Math.round(seconds)} s`;
    return `${Math.floor(seconds / 60)} min ${Math.round(seconds % 60)} s`;
  };

  return (
    <div className="liquid-glass rounded-xl p-6">
      <h3 className="text-lg font-semibold text-white flex items-center gap-2 mb-4">
        <BarChart3 className="w-5 h-5 text-brand-green" />
        {t('llmMonitor.orchestratorMetrics')}
      </h3>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-black/30 rounded-lg p-4 text-center min-w-0">
          <div className="text-2xl font-bold text-brand-green">
            {metrics?.total_requests || 0}
          </div>
          <div className="text-xs text-gray-200 truncate" title={t('llmMonitor.totalRequests')}>{t('llmMonitor.totalRequests')}</div>
        </div>

        <div className="bg-black/30 rounded-lg p-4 text-center min-w-0">
          <div className="text-2xl font-bold text-purple-400">
            {metrics?.total_swaps || 0}
          </div>
          <div className="text-xs text-gray-200 truncate" title={t('llmMonitor.modelSwaps')}>{t('llmMonitor.modelSwaps')}</div>
        </div>

        <div className="bg-black/30 rounded-lg p-4 text-center min-w-0">
          <div className="text-2xl font-bold text-cyan-400">
            {formatSwapTime(metrics?.average_swap_time_seconds)}
          </div>
          <div className="text-xs text-gray-200 truncate" title={t('llmMonitor.avgSwapTime')}>{t('llmMonitor.avgSwapTime')}</div>
        </div>

        <div className="bg-black/30 rounded-lg p-4 text-center min-w-0">
          <div className="text-2xl font-bold text-yellow-400">
            {Object.values(metrics?.model_statuses || {}).reduce(
              (sum, m) => sum + (m.request_count || 0), 0
            )}
          </div>
          <div className="text-xs text-gray-200 truncate" title={t('llmMonitor.inferences')}>{t('llmMonitor.inferences')}</div>
        </div>
      </div>
    </div>
  );
};

// Main Component
const LLMMonitor = () => {
  const { t } = useI18n();
  const [gpuStatus, setGpuStatus] = useState(null);
  const [metrics, setMetrics] = useState(null);
  const [models, setModels] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [selectedSession, setSelectedSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [wsConnected, setWsConnected] = useState(false);
  const wsRef = useRef(null);

  // Fetch initial data
  const fetchData = useCallback(async () => {
    try {
      setError(null); // Clear previous errors
      const [gpuRes, poolRes, sessionsRes] = await Promise.all([
        apiClient.get('/llm/gpu/status'),
        apiClient.get('/llm/gpu/pool'),
        apiClient.get('/llm/sessions')
      ]);

      const gpuData = gpuRes.data;
      const poolData = poolRes.data;
      const sessionsData = sessionsRes.data;

      setGpuStatus(gpuData);

      // Get models from GPU pool
      if (poolData?.success && poolData?.servers) {
        // Combine models from all servers
        const allModels = new Set();
        poolData.servers.forEach(server => {
          (server.models_loaded || []).forEach(model => allModels.add(model));
        });
        setModels(Array.from(allModels).map(name => ({
          name,
          id: name,
          role: name.includes('sylink') ? 'Tactical Coordinator' : 'General',
          description: `Model ${name}`,
          location: 'GPU',
          vram_required_mb: 8000
        })));
      }

      setSessions(sessionsData?.sessions || []);

      // Get metrics
      const metricsRes = await apiClient.get('/llm/gpu/metrics');
      const metricsData = metricsRes.data;
      setMetrics({
        ...metricsData,
        pool: poolData?.pool,
        servers: poolData?.servers
      });
    } catch (err) {
      // Silently handle 401 errors (user will be redirected to login by API client)
      if (err.response?.status === 401 || err.isAuthRedirect) {
        return;
      }
      console.error('Failed to fetch LLM data:', err);
      setError(t('llmMonitor.loadStatusFailed'));
      toast.error(t('llmMonitor.loadStatusFailed'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  // WebSocket connection
  useEffect(() => {
    const connectWebSocket = () => {
      const wsUrl = `${window.location.protocol === 'https:' ? 'wss:' : 'ws:'}//${window.location.host}/ws/llm?subscription=all`;

      wsRef.current = new WebSocket(wsUrl);

      wsRef.current.onopen = () => {
        setWsConnected(true);
        console.log('LLM WebSocket connected');
      };

      wsRef.current.onmessage = (event) => {
        const data = JSON.parse(event.data);

        if (data.type === 'gpu_status' || data.type === 'initial_status') {
          setGpuStatus(data.data.gpu);
          setMetrics(data.data.metrics);
        } else if (data.type === 'inference_update') {
          // Handle inference updates
          toast.info(`${data.data.model}: ${data.data.status}`);
        } else if (data.type === 'checkpoint') {
          toast.success(`Checkpoint: ${data.data.decision}`);
        }
      };

      wsRef.current.onclose = () => {
        setWsConnected(false);
        // Reconnect after delay
        setTimeout(connectWebSocket, 5000);
      };

      wsRef.current.onerror = (error) => {
        console.error('WebSocket error:', error);
      };
    };

    fetchData();
    connectWebSocket();

    return () => {
      if (wsRef.current) {
        wsRef.current.close();
      }
    };
  }, [fetchData]);

  // Swap model
  const handleSwapModel = async (modelId) => {
    try {
      const modelName = modelId.split(':')[0];
      await apiClient.post(`/llm/gpu/swap?model=${modelName}`);
      toast.success(t('llmMonitor.loadingModel', { model: modelName }));
      fetchData();
    } catch (error) {
      // Silently handle 401 errors (user will be redirected to login by API client)
      if (error.response?.status === 401 || error.isAuthRedirect) {
        return;
      }
      toast.error(t('llmMonitor.swapFailed'));
    }
  };

  if (loading) {
    return (
      <div className="container mx-auto px-6 py-8">
        <div className="flex items-center justify-center h-64">
          <RefreshCw className="w-8 h-8 text-brand-green animate-spin" />
        </div>
      </div>
    );
  }

  // Show error state if no data could be loaded
  if (error && !gpuStatus && !metrics) {
    return (
      <div className="container mx-auto px-6 py-8">
        <div className="flex flex-col items-center justify-center h-64 text-center">
          <AlertTriangle className="w-12 h-12 text-yellow-400 mb-4" />
          <h2 className="text-xl font-semibold text-white mb-2">{t('llmMonitor.serviceUnavailable')}</h2>
          <p className="text-gray-400 mb-4">{error}</p>
          <button
            onClick={fetchData}
            className="flex items-center gap-2 px-4 py-2 bg-brand-green/20 text-brand-green rounded-lg hover:bg-brand-green/30 transition-colors"
          >
            <RefreshCw className="w-4 h-4" />
            {t('llmMonitor.refresh')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-6 py-8">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold text-white flex items-center gap-3">
            <Brain className="w-8 h-8 text-brand-green" />
            {t('llmMonitor.title')}
          </h1>
          <p className="text-gray-400 mt-1">
            {t('llmMonitor.subtitle')}
          </p>
        </div>

        <div className="flex items-center gap-4">
          <div className={`flex items-center gap-2 px-3 py-1 rounded-full ${
            wsConnected ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'
          }`}>
            <div className={`w-2 h-2 rounded-full ${wsConnected ? 'bg-green-400 animate-pulse' : 'bg-red-400'}`} />
            {wsConnected ? t('llmMonitor.connected') : t('llmMonitor.disconnected')}
          </div>
          <button
            onClick={fetchData}
            className="flex items-center gap-2 px-4 py-2 bg-brand-green/20 text-brand-green rounded-lg hover:bg-brand-green/30 transition-colors"
          >
            <RefreshCw className="w-4 h-4" />
            {t('llmMonitor.refresh')}
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left Column - GPU & Metrics */}
        <div className="lg:col-span-2 space-y-6">
          <GPUStatusCard gpuData={gpuStatus} t={t} />
          <GPUPoolCard pool={metrics?.pool} servers={metrics?.servers} t={t} />
          <MetricsCard metrics={metrics} t={t} />

          {/* Models Grid */}
          <div className="liquid-glass rounded-xl p-6">
            <h3 className="text-lg font-semibold text-white flex items-center gap-2 mb-4">
              <Shield className="w-5 h-5 text-brand-green" />
              {t('llmMonitor.availableModels')}
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {models.map(model => (
                <ModelCard
                  key={model.id}
                  model={model}
                  isActive={gpuStatus?.current_model === model.id}
                  onSwap={handleSwapModel}
                  t={t}
                />
              ))}
            </div>
          </div>
        </div>

        {/* Right Column - Sessions */}
        <div className="space-y-6">
          <div className="liquid-glass rounded-xl p-6">
            <h3 className="text-lg font-semibold text-white flex items-center gap-2 mb-4">
              <Terminal className="w-5 h-5 text-brand-green" />
              {t('llmMonitor.activeSessions')}
            </h3>

            {sessions.length === 0 ? (
              <div className="text-center py-8 border border-dashed border-gray-700 rounded-xl bg-gray-900/30">
                {/* Status badge - Visible at top */}
                <div className="inline-flex items-center gap-2 px-3 py-1.5 mb-4 bg-blue-500/10 border border-blue-500/30 rounded-full text-sm font-medium text-blue-400">
                  <div className="w-2 h-2 rounded-full bg-blue-400 animate-pulse" />
                  {t('llmMonitor.readyStatus')}
                </div>

                {/* Icon */}
                <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gradient-to-br from-brand-green/20 to-cyan-500/20 flex items-center justify-center ring-2 ring-brand-green/30">
                  <Brain className="w-8 h-8 text-brand-green" />
                </div>

                {/* Title */}
                <p className="text-white font-medium mb-2">{t('llmMonitor.noActiveSessions')}</p>

                {/* Description */}
                <p className="text-sm text-gray-400 mb-4 max-w-xs mx-auto">{t('llmMonitor.noSessionsHint')}</p>

                {/* How to start indicator */}
                <div className="flex flex-col items-center gap-2">
                  <p className="text-sm text-gray-300">{t('llmMonitor.howToStart')}</p>
                  <div className="inline-flex items-center gap-2 px-4 py-2 bg-gray-800/80 text-gray-300 rounded-lg text-sm border border-gray-700/50">
                    <Clock className="w-4 h-4" />
                    {t('llmMonitor.waitingForScan')}
                  </div>
                </div>
              </div>
            ) : (
              <div className="space-y-3 max-h-96 overflow-y-auto">
                {sessions.map(session => (
                  <SessionCard
                    key={session.session_id}
                    session={session}
                    onClick={setSelectedSession}
                    t={t}
                  />
                ))}
              </div>
            )}
          </div>

          {/* Actions Rapides */}
          <div className="liquid-glass rounded-xl p-6">
            <h3 className="text-lg font-semibold text-white flex items-center gap-2 mb-4">
              <Activity className="w-5 h-5 text-brand-green" />
              {t('llmMonitor.quickActions')}
            </h3>
            <div className="space-y-3">
              <button className="w-full flex items-center gap-3 px-4 py-3 bg-brand-green/10 text-brand-green rounded-lg hover:bg-brand-green/20 transition-colors">
                <Play className="w-4 h-4" />
                {t('llmMonitor.newSession')}
              </button>
              <button className="w-full flex items-center gap-3 px-4 py-3 bg-purple-500/10 text-purple-400 rounded-lg hover:bg-purple-500/20 transition-colors">
                <CheckCircle className="w-4 h-4" />
                {t('llmMonitor.requestCheckpoint')}
              </button>
              <button className="w-full flex items-center gap-3 px-4 py-3 bg-orange-500/10 text-orange-400 rounded-lg hover:bg-orange-500/20 transition-colors">
                <Key className="w-4 h-4" />
                {t('llmMonitor.hashcatTasks')}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Session Detail Modal would go here */}
    </div>
  );
};

export default LLMMonitor;
